import { Divider, Grid, Typography, withStyles } from '@material-ui/core';
import React from 'react'; // eslint-disable-line
import { Field } from 'react-final-form';
import { connect } from 'react-redux';
import { getClaim } from '../../../../../claim';
import { ClaimType } from '../../../../../claim/claim-types';
import { BenefitCategory, BenefitCategoryDread } from '../../../../../codes/benefit-category';
import { ClaimConditions, ClaimConditionsDread } from '../../../../../codes/claim-condition';
import { CurrentTreatment } from '../../../../../codes/claim-event';
import { Conditions } from '../../../../../codes/conditions';
import {
    DetailedClaimConditions,
    DetailedClaimConditionsDread,
} from '../../../../../codes/detailed-claim-condition';
import { MajorClaimCause } from '../../../../../codes/major-claim-cause';
import {
    Condition,
    FullWidthDatePicker,
    FullWidthRadioGroup,
    FullWidthSearchSelectField,
    FullWidthTextField,
    RadioOption,
    True,
    validations,
    WhenFieldChanges,
} from '../../../../../forms';
import {
    FirstNameMask,
    InitialsMask,
    LastNameMask,
    TelephoneNumberMask,
} from '../../../../../forms/input-masks';
import { requiredValidator } from '../../../../../forms/validations';
import {
    maxDateValidator,
    minDateValidator,
} from '../../../../../forms/validations/date-validator';
import { State } from '../../../../../redux/root-reducer';
import { Nullable } from '../../../../../shared-types';
import styles, { StyledComponent } from '../../../../../styles/styles';

type Props = StyledComponent & {
    claimType: Nullable<ClaimType>;
    heading: React.ReactNode;
    showTreatingSpecialist?: boolean;
};
const ClaimDetailsForm: React.FunctionComponent<Props> = ({
    claimType,
    classes,
    heading,
    showTreatingSpecialist,
}: Props) => {
    const { categoryOptions, claimConditionsOptions, detailedClaimConditions } = getClaimOptions(
        claimType
    );

    const [selectedMajorClaimCause, setSelectedMajorClaimCause] = React.useState<string>('');
    const [selectedCategory, setSelectedCategory] = React.useState<string>('');
    const [selectedClaimCondition, setSelectedClaimCondition] = React.useState<string>('');
    const [newSelectionsMajorCause, setNewSelectionMajorCause] = React.useState<boolean>(false);
    const [newSelectionsCategory, setNewSelectionCategory] = React.useState<boolean>(false);
    const [isCategoryEmpty, setisCategoryEmpty] = React.useState<boolean>(false);
    const [oldSelectedMajorClaimCause, setOldSelectedMajorClaimCause] = React.useState<string>('');
    const [oldSelectedCategory, setOldSelectedCategory] = React.useState<string>('');
    const [oldSelectedClaimCondition, setOldSelectedClaimCondition] = React.useState<string>('');
    const [
        oldSelectedDetailedClaimCondition,
        setOldSelectedDetailedClaimCondition,
    ] = React.useState<string>('');

    const getMajorClaimCauseOptions = (currentValue: string) => {
        if (!MajorClaimCause || !Array.isArray(MajorClaimCause)) {
            return [];
        }

        const currentOptionExists = MajorClaimCause.some(option => option.value === currentValue);
        const filteredMajorClaimCause = MajorClaimCause.filter(cause =>
            categoryOptions?.some(benefit => benefit.Category === cause.category)
        );

        const currentMajorClaim =
            MajorClaimCause &&
            filteredMajorClaimCause.find(option => option.category === currentValue);

        const setValue = currentOptionExists
            ? filteredMajorClaimCause
            : [
                  ...filteredMajorClaimCause,
                  {
                      value: currentValue,
                      label: currentValue,
                      category: 'Unknown',
                  },
              ];

        if (currentMajorClaim) {
            setSelectedMajorClaimCause(currentMajorClaim?.category || '');
        }

        const visibleOptions = setValue.filter(option => option.value && option.label);

        const dynamicOption = currentOptionExists
            ? []
            : [{ value: currentValue, label: currentValue }];

        if (visibleOptions.length === 0) {
            return [{ value: 'Not applicable', label: 'Not applicable' }];
        }
        return [...dynamicOption, ...visibleOptions];
    };

    const getCategoryOptions = (currentValue: string) => {
        if (currentValue === '') return [];
        let filteredCategoryOptions: typeof categoryOptions = [];

        if (!categoryOptions || !Array.isArray(categoryOptions)) {
            return filteredCategoryOptions;
        }

        if (!selectedMajorClaimCause) {
            filteredCategoryOptions = categoryOptions;
        } else {
            filteredCategoryOptions = categoryOptions.filter(
                option => option?.Category === currentValue
            );
        }

        const currentOptionExists = categoryOptions.some(
            option => option.Category === currentValue
        );

        const visibleOptions = filteredCategoryOptions.filter(
            option => option.value && option.label
        );

        const dynamicOption = currentOptionExists
            ? []
            : currentValue
            ? [{ value: currentValue, label: currentValue }]
            : [];

        const result =
            visibleOptions.length === 0
                ? [...dynamicOption, { value: 'Not applicable', label: 'Not applicable' }]
                : [...dynamicOption, ...visibleOptions];

        const uniqueOptions = Array.from(
            new Map(result.map(option => [option.value, option])).values()
        );

        return uniqueOptions;
    };

    const filteredCategoryOptions = getCategoryOptions(
        isCategoryEmpty ? '' : selectedMajorClaimCause
    );

    const setCategorySelection = (currentValue: string) => {
        if (
            claimType === ClaimType.Disability ||
            claimType === ClaimType.Dread ||
            claimType === ClaimType.TerminalIllness
        ) {
            setSelectedCategory(newSelectionsMajorCause ? '' : currentValue || '');
        } else {
            setSelectedCategory(currentValue || '');
        }
    };

    const getClaimConditionOptions = (currentValue: string) => {
        if (currentValue === '') return [];

        let claimConditionOptions: typeof claimConditionsOptions = [];

        if (!claimConditionsOptions || !Array.isArray(claimConditionsOptions)) {
            return claimConditionOptions;
        }

        claimConditionOptions = claimConditionsOptions.filter(
            option => option.Category === currentValue
        );

        const currentOptionExists = claimConditionsOptions.some(
            option => option.Category === currentValue
        );

        const visibleOptions = claimConditionOptions.filter(option => option.value && option.label);

        const dynamicOption = currentOptionExists
            ? []
            : currentValue
            ? [{ value: '', label: '', category: '' }]
            : [];

        if (visibleOptions.length === 0) {
            visibleOptions.push({ value: 'Not applicable', label: 'Not applicable', Category: '' });
        }

        const combinedOptions = [...dynamicOption, ...visibleOptions];
        const uniqueOptions = Array.from(
            new Map(combinedOptions.map(option => [option.value, option])).values()
        );

        return uniqueOptions;
    };

    const claimConditionOptions = getClaimConditionOptions(
        newSelectionsMajorCause ? '' : selectedCategory
    );

    const setclaimConditionSelection = (currentValue: string) => {
        setSelectedClaimCondition(currentValue || '');
    };

    const getClaimConditionDetailOptions = (currentValue: string) => {
        if (currentValue === '') return [];
        let filteredClaimConditionDetailsOptions: typeof detailedClaimConditions = [];

        if (!detailedClaimConditions || !Array.isArray(detailedClaimConditions)) {
            return filteredClaimConditionDetailsOptions;
        }

        if (!selectedMajorClaimCause) {
            filteredClaimConditionDetailsOptions = detailedClaimConditions;
        } else {
            filteredClaimConditionDetailsOptions = detailedClaimConditions.filter(
                option => option.Category === currentValue
            );
        }

        const currentOptionExists = detailedClaimConditions.some(
            option => option.Category === currentValue
        );

        const visibleOptions = filteredClaimConditionDetailsOptions.filter(
            option => option.value && option.label
        );

        const dynamicOption = currentOptionExists
            ? [{ value: '', label: '', category: '' }]
            : currentValue
            ? [{ value: '', label: '', category: '' }]
            : [];

        if (visibleOptions.length === 0) {
            return [...dynamicOption, { value: 'Not applicable', label: 'Not applicable' }];
        }

        const allOptions = [...dynamicOption, ...visibleOptions];

        const uniqueOptions = allOptions.filter(
            (option, index, self) =>
                index === self.findIndex(o => o.value === option.value && o.label === option.label)
        );

        return uniqueOptions;
    };

    const filteredClaimConditionDetailsOptions = getClaimConditionDetailOptions(
        newSelectionsCategory || newSelectionsMajorCause ? '' : selectedClaimCondition
    );

    const getUniqueListValues = (list: { value: any }[]) => {
        return Array.from(new Map(list.map(item => [item.value, item])).values()).sort((a, b) =>
            a.value > b.value ? 1 : -1
        );
    };

    return (
        <React.Fragment>
            <Typography variant="h5">{heading} </Typography>
            <Divider></Divider>
            <Grid container spacing={5} justify="flex-start">
                <Grid item xs={6}>
                    <FullWidthDatePicker
                        name="dateOfEvent"
                        validate={validations.composeValidators(
                            validations.requiredValidator,
                            maxDateValidator('dateClaimReported')
                        )}
                        maxDateField="dateClaimReported"
                        disableFuture
                    />
                </Grid>
                <Grid item xs={6}>
                    <FullWidthDatePicker
                        name="dateClaimReported"
                        validate={validations.composeValidators(
                            validations.requiredValidator,
                            minDateValidator('dateOfEvent')
                        )}
                        minDateField="dateOfEvent"
                        disableFuture
                    />
                </Grid>
                <Grid item xs={6}>
                    <FullWidthRadioGroup name="medicalConditionType" validate={requiredValidator}>
                        <RadioOption value="Disease" />
                        <RadioOption value="Accident" />
                    </FullWidthRadioGroup>
                    <WhenFieldChanges
                        field="medicalConditionType"
                        set="condition"
                        to={(type: string) => (type === 'Disease' ? 'Natural' : null)}
                    />
                </Grid>
                <Condition when="medicalConditionType" is="Accident">
                    <True>
                        <Grid item xs={6}>
                            <FullWidthRadioGroup
                                label="Accident reported to the police"
                                name="reportedToPolice"
                                validate={requiredValidator}
                            >
                                <RadioOption value="Yes" />
                                <RadioOption value="No" />
                            </FullWidthRadioGroup>
                        </Grid>
                    </True>
                </Condition>
                <WhenFieldChanges field="medicalConditionType" set="reportedToPolice" to={null} />
                <WhenFieldChanges field="reportedToPolice" set="caseNumber" to={null} />
                <WhenFieldChanges field="reportedToPolice" set="accidentDetails" to={null} />
                <WhenFieldChanges field="reportedToPolice" set="policeStation" to={null} />
                <Condition when="reportedToPolice" is="Yes">
                    <True>
                        <Grid item xs={6}>
                            <FullWidthTextField name="policeStation" />
                        </Grid>
                        <Grid item xs={6}>
                            <FullWidthTextField name="caseNumber" />
                        </Grid>
                        <Grid item xs={6}>
                            <FullWidthTextField
                                name="accidentDetails"
                                label="Accident details"
                                multiline
                            />
                        </Grid>
                    </True>
                </Condition>
                <Grid item xs={12}>
                    <Typography variant="subtitle2" className={classes.marginTop}>
                        Cause of Claim
                    </Typography>
                </Grid>
                <Field
                    name="medicalConditionType"
                    subscription={{ value: true }}
                    component={({ input: { value: medicalConditionType } }) => {
                        let options = Conditions.filter(x => x.value !== 'Natural');
                        let disabled = false;
                        if (medicalConditionType === 'Disease') {
                            disabled = true;
                            options = Conditions;
                        }
                        return (
                            <Grid item xs={6}>
                                <FullWidthSearchSelectField
                                    label="Natural/Unnatural"
                                    name="condition"
                                    options={options}
                                    validate={requiredValidator}
                                    disabled={disabled}
                                />
                            </Grid>
                        );
                    }}
                />
                <Grid item xs={6}>
                    <Field
                        name="majorClaimCause"
                        subscription={{ value: true }}
                        component={({
                            input: { value: currentValue, onChange, ...restInput },
                            form,
                        }) => {
                            const options = getMajorClaimCauseOptions(currentValue);
                            setSelectedMajorClaimCause(currentValue);
                            if (
                                currentValue !== '' &&
                                currentValue !== undefined &&
                                currentValue !== oldSelectedMajorClaimCause
                            ) {
                                setOldSelectedMajorClaimCause(currentValue);
                            }
                            return (
                                <FullWidthSearchSelectField
                                    label="Major claim cause"
                                    name="majorClaimCause"
                                    helperText={
                                        (claimType === ClaimType.Disability ||
                                            claimType === ClaimType.Dread ||
                                            claimType === ClaimType.TerminalIllness) &&
                                        (oldSelectedMajorClaimCause &&
                                        !MajorClaimCause.some(
                                            option => option.value === oldSelectedMajorClaimCause
                                        ) &&
                                        oldSelectedMajorClaimCause !== 'Not applicable' &&
                                        oldSelectedMajorClaimCause !== '' ? (
                                            <span>
                                                Previously selected value: `
                                                <strong>{oldSelectedMajorClaimCause}</strong>` (no
                                                longer available in options)
                                            </span>
                                        ) : (
                                            ''
                                        ))
                                    }
                                    options={
                                        claimType === ClaimType.Disability ||
                                        claimType === ClaimType.Dread ||
                                        claimType === ClaimType.TerminalIllness
                                            ? options
                                            : getUniqueListValues(MajorClaimCause)
                                    }
                                    value={currentValue}
                                    onChange={(e: any) => {
                                        currentValue = e;
                                        if (
                                            claimType === ClaimType.Disability ||
                                            claimType === ClaimType.Dread ||
                                            claimType === ClaimType.TerminalIllness
                                        ) {
                                            setNewSelectionMajorCause(true);
                                            setSelectedCategory('');
                                            setSelectedClaimCondition('');

                                            if (currentValue === undefined) {
                                                setisCategoryEmpty(true);
                                            } else {
                                                setisCategoryEmpty(false);
                                            }
                                        }
                                    }}
                                    validate={requiredValidator}
                                />
                            );
                        }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Field
                        name="claimCategory"
                        subscription={{ value: true }}
                        component={({ input: { value: currentValue, onChange, ...restInput } }) => {
                            setCategorySelection(currentValue);
                            if (
                                currentValue !== '' &&
                                currentValue !== undefined &&
                                currentValue !== oldSelectedCategory
                            ) {
                                setOldSelectedCategory(currentValue);
                            }
                            return (
                                <FullWidthSearchSelectField
                                    label="Claim Category"
                                    name="claimCategory"
                                    helperText={
                                        (claimType === ClaimType.Disability ||
                                            claimType === ClaimType.Dread ||
                                            claimType === ClaimType.TerminalIllness) &&
                                        (oldSelectedCategory &&
                                        !filteredCategoryOptions.some(
                                            option => option.value === currentValue
                                        ) &&
                                        oldSelectedCategory !== 'Not applicable' &&
                                        oldSelectedCategory !== '' ? (
                                            <span>
                                                Previously selected value: `
                                                <strong>{oldSelectedCategory}</strong>` (no longer
                                                available in options)
                                            </span>
                                        ) : (
                                            ''
                                        ))
                                    }
                                    options={
                                        claimType === ClaimType.Disability ||
                                        claimType === ClaimType.Dread ||
                                        claimType === ClaimType.TerminalIllness
                                            ? filteredCategoryOptions
                                            : getUniqueListValues(categoryOptions)
                                    }
                                    value={newSelectionsMajorCause ? '' : currentValue}
                                    onChange={(e: any) => {
                                        if (
                                            claimType === ClaimType.Disability ||
                                            claimType === ClaimType.Dread ||
                                            claimType === ClaimType.TerminalIllness
                                        ) {
                                            setNewSelectionMajorCause(false);
                                            setNewSelectionCategory(true);
                                        }
                                    }}
                                    validate={
                                        claimType === ClaimType.Disability ||
                                        claimType === ClaimType.Dread ||
                                        claimType === ClaimType.TerminalIllness
                                            ? requiredValidator
                                            : ''
                                    }
                                />
                            );
                        }}
                    />
                </Grid>

                <Condition when="claimCategory" is="Other">
                    <True>
                        <Grid item xs={6}>
                            <FullWidthTextField
                                name="claimCategoryDesc"
                                label="Claim category (free text)"
                                validate={requiredValidator}
                            />
                        </Grid>
                    </True>
                </Condition>
                <Grid item xs={6}>
                    <FullWidthTextField label="Comments on cause" name="comments" multiline />
                </Grid>
                <Grid item xs={12}>
                    <Field
                        name="claimCondition"
                        subscription={{ value: true }}
                        component={({ input: { value: currentValue, onChange, ...restInput } }) => {
                            setclaimConditionSelection(currentValue);
                            if (
                                currentValue !== '' &&
                                currentValue !== undefined &&
                                currentValue !== oldSelectedClaimCondition
                            ) {
                                setOldSelectedClaimCondition(currentValue);
                            }
                            return (
                                <FullWidthSearchSelectField
                                    label="Claim Condition"
                                    name="claimCondition"
                                    helperText={
                                        (claimType === ClaimType.Disability ||
                                            claimType === ClaimType.Dread ||
                                            claimType === ClaimType.TerminalIllness) &&
                                        (oldSelectedClaimCondition &&
                                        !claimConditionOptions.some(
                                            option => option.value === oldSelectedClaimCondition
                                        ) &&
                                        oldSelectedClaimCondition !== 'Not applicable' &&
                                        oldSelectedClaimCondition !== '' ? (
                                            <span>
                                                Previously selected value: `
                                                <strong>{oldSelectedClaimCondition}</strong>` (no
                                                longer available in options)
                                            </span>
                                        ) : (
                                            ''
                                        ))
                                    }
                                    fullWidth
                                    options={
                                        claimType === ClaimType.Disability ||
                                        claimType === ClaimType.Dread ||
                                        claimType === ClaimType.TerminalIllness
                                            ? claimConditionOptions
                                            : getUniqueListValues(claimConditionsOptions)
                                    }
                                    onChange={(e: any) => {
                                        if (
                                            claimType === ClaimType.Disability ||
                                            claimType === ClaimType.Dread ||
                                            claimType === ClaimType.TerminalIllness
                                        ) {
                                            setNewSelectionCategory(false);
                                        }
                                    }}
                                    value={currentValue}
                                    validate={
                                        claimType === ClaimType.Disability ||
                                        claimType === ClaimType.Dread ||
                                        claimType === ClaimType.TerminalIllness
                                            ? requiredValidator
                                            : ''
                                    }
                                />
                            );
                        }}
                    />
                </Grid>
                <Condition when="claimCondition" is="Other">
                    <True>
                        <Grid item xs={12}>
                            <FullWidthTextField
                                name="claimConditionDesc"
                                label="Claim condition (free text)"
                                validate={requiredValidator}
                            />
                        </Grid>
                    </True>
                </Condition>
                <Grid item xs={12}>
                    <Field
                        name="detailedClaimCondition"
                        subscription={{ value: true }}
                        component={({ input: { value: currentValue, onChange, ...restInput } }) => {
                            if (
                                currentValue !== '' &&
                                currentValue !== undefined &&
                                currentValue !== oldSelectedDetailedClaimCondition
                            ) {
                                setOldSelectedDetailedClaimCondition(currentValue);
                            }
                            return (
                                <FullWidthSearchSelectField
                                    label="Detailed Claim Condition"
                                    name="detailedClaimCondition"
                                    helperText={
                                        (claimType === ClaimType.Disability ||
                                            claimType === ClaimType.Dread ||
                                            claimType === ClaimType.TerminalIllness) &&
                                        (oldSelectedDetailedClaimCondition &&
                                        !filteredClaimConditionDetailsOptions.some(
                                            option =>
                                                option.value === oldSelectedDetailedClaimCondition
                                        ) &&
                                        oldSelectedDetailedClaimCondition !== 'Not applicable' &&
                                        oldSelectedDetailedClaimCondition !== '' ? (
                                            <span>
                                                Previously selected value: `
                                                <strong>{oldSelectedDetailedClaimCondition}</strong>
                                                ` (no longer available in options)
                                            </span>
                                        ) : (
                                            ''
                                        ))
                                    }
                                    options={
                                        claimType === ClaimType.Disability ||
                                        claimType === ClaimType.Dread ||
                                        claimType === ClaimType.TerminalIllness
                                            ? filteredClaimConditionDetailsOptions
                                            : getUniqueListValues(detailedClaimConditions)
                                    }
                                    value={currentValue}
                                    onChange={onChange}
                                    validate={requiredValidator}
                                />
                            );
                        }}
                    />
                </Grid>

                {(claimType === ClaimType.Disability ||
                    claimType === ClaimType.Dread ||
                    claimType === ClaimType.TerminalIllness) && (
                    <>
                        <WhenFieldChanges field="majorClaimCause" set="claimCategory" to={''} />
                        <WhenFieldChanges field="majorClaimCause" set="claimCondition" to={''} />
                        <WhenFieldChanges
                            field="claimCategory"
                            set="detailedClaimCondition"
                            to={''}
                        />
                        <WhenFieldChanges field="claimCategory" set="claimCondition" to={''} />
                        <WhenFieldChanges
                            field="claimCondition"
                            set="detailedClaimCondition"
                            to={''}
                        />
                        <WhenFieldChanges
                            field="majorClaimCause"
                            set="detailedClaimCondition"
                            to={''}
                        />
                    </>
                )}

                <Condition when="detailedClaimCondition" is="Other">
                    <True>
                        <Grid item xs={6}>
                            <FullWidthTextField
                                name="detailedClaimConditionDesc"
                                label="Detailed claim condition (free text)"
                                validate={requiredValidator}
                            />
                        </Grid>
                    </True>
                </Condition>
                <Grid item xs={12}>
                    <Typography variant="subtitle2" className={classes.marginTop}>
                        Treatment
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <FullWidthSearchSelectField
                        name="currentTreatmentPlan"
                        label="Current treatment plan"
                        options={CurrentTreatment}
                    />
                </Grid>
                <WhenFieldChanges
                    field="currentTreatmentPlan"
                    becomes="Other"
                    set="highLevelDescriptionOfTreatmentOther"
                    to={null}
                />
                <Condition when="currentTreatmentPlan" is="Other">
                    <True>
                        <Grid item xs={6}>
                            <FullWidthTextField
                                label="High-level description of the current treatment plan"
                                name="highLevelDescriptionOfTreatmentOther"
                                validate={requiredValidator}
                                multiline
                            />
                        </Grid>
                    </True>
                </Condition>
                <Grid item xs={6}>
                    <FullWidthTextField
                        label="Detailed description of current treatment Plan"
                        name="descriptionOfCurrentTreatmentPlan"
                        multiline
                    />
                </Grid>
                <Grid item xs={6}>
                    <FullWidthTextField
                        label="Detailed description of Current Medication"
                        name="descriptionOfCurrentMedication"
                        multiline
                    />
                </Grid>
                {showTreatingSpecialist && (
                    <React.Fragment>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" className={classes.marginTop}>
                                Treating Specialist
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <FullWidthTextField
                                name="treatingSpecialistInitials"
                                mask={InitialsMask}
                                inputProps={{
                                    style: { textTransform: 'uppercase' },
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FullWidthTextField
                                name="treatingSpecialistName"
                                validate={validations.requiredValidator}
                                mask={FirstNameMask}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FullWidthTextField
                                name="treatingSpecialistSurname"
                                validate={validations.requiredValidator}
                                mask={LastNameMask}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FullWidthTextField
                                name="treatingSpecialistTelephoneNumber"
                                mask={TelephoneNumberMask}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FullWidthTextField
                                name="treatingSpecialistRegistrationNumber"
                                validate={requiredValidator}
                            />
                        </Grid>
                    </React.Fragment>
                )}
            </Grid>
        </React.Fragment>
    );
};

function getClaimOptions(claimType: Nullable<ClaimType>) {
    if (claimType === ClaimType.Dread) {
        return {
            categoryOptions: BenefitCategoryDread,
            claimConditionsOptions: ClaimConditionsDread,
            detailedClaimConditions: DetailedClaimConditionsDread,
        };
    }

    return {
        categoryOptions: BenefitCategory,
        claimConditionsOptions: ClaimConditions,
        detailedClaimConditions: DetailedClaimConditions,
    };
}

const mapStateToProps = (state: State) => ({
    claimType: getClaim(state).claimType,
});

export default connect(mapStateToProps, {})(withStyles(styles)(ClaimDetailsForm));
